/**
 * FlujoFormModal.tsx — Modal único de creación/edición de flujos.
 *
 * Round 2026-05-13 — reemplaza el modal genérico de /gastos (que pedía
 * elegir el `rol_de_aloja_facil` manualmente) por 3 modos pre-configurados
 * según el botón que abrió el modal:
 *
 *   - **Gasto**       → `rol='paga'`,           `pagado_por = AF` (lock).
 *                       Admin elige solo `pagado_a` (a quién paga AF).
 *   - **Ingreso**     → `rol='recibe'`,         `pagado_a = AF` (lock).
 *                       Admin elige solo `pagado_por` (quién paga a AF).
 *   - **Intermediario** → `rol='intermediario'`. Admin elige ambos
 *                         `pagado_por` y `pagado_a`, sin que AF esté en
 *                         ninguna punta (AF está en el medio del flujo).
 *
 * El backend acepta el shape completo (`pagadoPor` y `pagadoA` JSON) sin
 * cambios. Mismo endpoint que /gastos legacy.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tag,
  Tooltip,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getMsg } from "../../utils/antdStatic";
import { apiFetch } from "../../services/apiClient";
import { userSelectOption } from "../../utils/userDisplay";
import { formatPropertyName } from "../../utils/propertyDisplay";
import FlujoFilesManager from "./components/FlujoFilesManager";
import { FLUJO_ACCEPT, MAX_UPLOAD_BYTES } from "./components/FlujoFilesManager";
import { DragDropUpload, useClipboardPaste } from "../../Components/DragDropUpload";
import { idParaAdjuntos } from "./adjuntosFlujo";
import {
  type Flujo,
  type FlujoFile,
  type FlujoCreatePayload,
  type FlujoPair,
  type FlujoPartyTipo,
  type FlujoPresetMode,
  type PropiedadConPropietarios,
  CATEGORIA_LABEL,
  FLUJO_CATEGORIAS,
  crearFlujo,
  actualizarFlujo,
  actualizarFlujoIntermediario,
  listarPropietariosPropiedades,
  pagoEntidadAF,
  subirFlujoArchivo,
} from "./flujos.api";
import { ahoraChile } from "../../utils/fechaChile";

const { TextArea } = Input;

// ── Tipos de soporte ───────────────────────────────────────────────────────

interface UsuarioFinanzas {
  id: number;
  rol: string;
  nombre: string;
  propiedades?: string[];
}

interface PropiedadOpt {
  id: number;
  nombre: string;
}

interface ExpenseRecipient {
  id: number;
  nombre: string;
  activo: boolean;
}

// ── Roles disponibles según el modo ───────────────────────────────────────
//
// Cuando AF es uno de los lados (gasto/ingreso), la contraparte puede ser
// propietario, arrendatario, aseador, trabajador o receptor externo. AF
// NO aparece como opción de la contraparte (la regla pagador ≠ receptor
// es automática).
//
// En modo intermediario, ninguno de los lados puede ser AF (por
// definición). Ambos pueden ser cualquier otro rol.

type RolKey =
  | "propietario"
  | "arrendatario"
  | "aseador"
  | "trabajador"
  | "receptor_externo";

const ROL_OPTIONS: Array<{ value: RolKey; label: string; tipo: FlujoPartyTipo }> = [
  // `value: "propietario"` es la clave interna histórica de tipo=1; el label
  // visible es "Cliente" (round 2026-06-20): el flujo se ancla a la propiedad
  // y se muestra/guarda al cliente-gestor (quien AF liquida), no al dueño legal.
  { value: "propietario", label: "Cliente", tipo: 1 },
  { value: "arrendatario", label: "Arrendatario", tipo: 2 },
  { value: "aseador", label: "Aseador", tipo: 3 },
  { value: "trabajador", label: "Trabajador", tipo: 4 },
  { value: "receptor_externo", label: "Receptor externo", tipo: 5 },
];

const ROL_BY_TIPO: Record<number, RolKey | null> = {
  0: null, // AF — no se elige acá
  1: "propietario",
  2: "arrendatario",
  3: "aseador",
  4: "trabajador",
  5: "receptor_externo",
};

// ── Etiquetas por modo ─────────────────────────────────────────────────────

const MODE_META: Record<
  FlujoPresetMode,
  {
    title: string;
    titleEdit: string;
    color: string;
    bgColor: string;
    icon: string;
    alertMessage: string;
    alertDescription: string;
    sideLabel: string; // "Pagado a" para gasto, "Pagado por" para ingreso
    sideTooltip: string;
  }
> = {
  gasto: {
    title: "Nuevo gasto",
    titleEdit: "Editar gasto",
    color: "#cf1322",
    bgColor: "#fff1f0",
    icon: "💳",
    alertMessage: "Gasto — Aloja Fácil paga (egreso de caja)",
    alertDescription:
      "AF Empresa egresa este monto. Elige a quién se le paga (cliente, arrendatario, aseador, trabajador o receptor externo). El receptor externo es cualquier otra empresa que no usa el sistema (internet, impuestos, proveedores, contador); las lavanderías no van acá — se registran en /lavanderia.",
    sideLabel: "Pagado a (destinatario)",
    sideTooltip:
      "Persona o entidad que recibe el dinero. AF Empresa es el pagador implícito (fijo en gastos).",
  },
  ingreso: {
    title: "Nuevo ingreso",
    titleEdit: "Editar ingreso",
    color: "#389e0d",
    bgColor: "#f6ffed",
    icon: "💵",
    alertMessage: "Ingreso — Aloja Fácil cobra (entrada de caja)",
    alertDescription:
      "AF Empresa recibe este monto. Elige quién paga (cliente, arrendatario, aseador, trabajador o receptor externo). El receptor externo es cualquier otra empresa que no usa el sistema (internet, impuestos, proveedores, contador); las lavanderías no van acá — se registran en /lavanderia.",
    sideLabel: "Pagado por (emisor)",
    sideTooltip:
      "Persona o entidad que entrega el dinero. AF Empresa es el receptor implícito (fijo en ingresos).",
  },
  intermediario: {
    title: "Nuevo intermediario",
    titleEdit: "Editar intermediario",
    color: "#1677ff",
    bgColor: "#e6f4ff",
    icon: "🔁",
    alertMessage: "Intermediario — AF recibe y luego paga el mismo monto",
    alertDescription:
      "AF Empresa está en el medio del flujo: cobra al origen y entrega al destino. Al guardar se crearán DOS movimientos vinculados (un ingreso y un gasto), de modo que editar o eliminar uno cascadea al par. Neto AF = 0. Ninguna punta puede ser AF.",
    sideLabel: "Lados del intermediario",
    sideTooltip: "",
  },
};

// ── Props ──────────────────────────────────────────────────────────────────

interface FlujoFormModalProps {
  open: boolean;
  /** "gasto" | "ingreso" | "intermediario". Determina el preset del modal. */
  mode: FlujoPresetMode;
  /** Flujo a editar; si es null, modal en modo creación. */
  editing: Flujo | null;
  /**
   * Row par del editing cuando es un intermediario (lado opuesto del par).
   * Necesario para hidratar correctamente el form en modo edición:
   * el editing puede ser el lado 'recibe' (cuyo pagadoA es AF) o el lado
   * 'paga' (cuyo pagadoPor es AF), y la otra punta externa vive en el
   * par. Si no se provee, intentamos derivar todo desde `editing` solo
   * (pero quedaría incompleto).
   */
  editingPair?: Flujo | null;
  usuarios: UsuarioFinanzas[];
  propiedades: PropiedadOpt[];
  onCancel: () => void;
  /**
   * Callback al guardar exitosamente. Recibe el row principal y, si fue
   * un intermediario, el `pair` completo con ambos rows (ingreso + gasto)
   * para que el padre pueda actualizar el state local sin esperar un
   * refetch (evita la sensación de "solo aparece uno" al crear).
   */
  onSaved: (flujo: Flujo, pair?: FlujoPair) => void;
}

// ── Componente ─────────────────────────────────────────────────────────────

export default function FlujoFormModal({
  open,
  mode,
  editing,
  editingPair,
  usuarios,
  propiedades,
  onCancel,
  onSaved,
}: FlujoFormModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [archivosPendientes, setArchivosPendientes] = useState<File[]>([]);
  const agregarArchivo = useCallback((archivo: File) => {
    if (archivo.size > MAX_UPLOAD_BYTES) { getMsg()?.error(`"${archivo.name}" supera el límite de 100 MiB`); return; }
    setArchivosPendientes((prev) => [...prev, archivo]);
  }, []);
  useClipboardPaste({ active: open && !editing, onFile: agregarArchivo, accept: FLUJO_ACCEPT });
  useEffect(() => { if (!open) setArchivosPendientes([]); }, [open]);
  const [recipients, setRecipients] = useState<ExpenseRecipient[]>([]);
  // Catálogo property-centric para el selector "Propietario" (round
  // 2026-06-05): cada propiedad con sus dueños. Reemplaza la elección de
  // un usuario suelto por la elección de una propiedad + sus propietarios.
  const [propPropietarios, setPropPropietarios] = useState<
    PropiedadConPropietarios[]
  >([]);

  const meta = MODE_META[mode];

  // Round 2026-06-01 — flujos derivados de inventario (inventoryTxId !=
  // null) tienen su monto/categoría como read-only: la fuente de verdad
  // es el `Inventory` row, no `gastos.monto`. Si admin quiere cambiar
  // monto/cantidad/precio, debe editar el producto en /inventario y el
  // backend (updateItem → recalcInventoryDerivedFlowsFromInventory)
  // propaga el cambio aquí. Permitir editar acá rompería la sincro.
  // Ver docs/flujo-financiero.md §10.8.5.
  const esFlujoDerivadoInventario = editing?.inventoryTxId != null;

  // Round 2026-06-19 — modelo DECOUPLED: el cobro al cliente (RECUPERACIÓN de
  // inventario, rol='recibe') SÍ tiene monto editable (es un evento separado
  // del egreso: cobrar el costo, parcial, más, o $0 = regalo). El egreso/
  // reembolso (rol='paga') sigue read-only y se sincroniza desde /inventario.
  const esRecuperacionInventario =
    esFlujoDerivadoInventario && editing?.rolDeAlojaFacil === "recibe";
  // El monto es read-only solo para el lado egreso/reembolso; la recuperación
  // se puede editar.
  const montoReadOnly = esFlujoDerivadoInventario && !esRecuperacionInventario;

  // Carga catálogo de receptores externos al abrir.
  useEffect(() => {
    if (!open) return;
    apiFetch<{ data: ExpenseRecipient[] }>(
      "/finanzas/empresa/recipients",
      { method: "GET" },
    ).then((r) => {
      const arr = Array.isArray(r?.data) ? r!.data : [];
      setRecipients(arr.filter((x) => x.activo));
    });
  }, [open]);

  // Carga el catálogo property-centric al abrir. Cada entrada es una propiedad
  // con su cliente-gestor (con login). Alimenta el selector de contraparte
  // tipo=1 ("Cliente") del modal.
  useEffect(() => {
    if (!open) return;
    listarPropietariosPropiedades().then((arr) => setPropPropietarios(arr));
  }, [open]);

  // Reset del form al cambiar editing o mode o al abrir.
  //
  // En modo INTERMEDIARIO la edición es especial: el `editing` puede ser
  // el lado 'recibe' (pagadoPor=externo origen, pagadoA=AF) o el lado
  // 'paga' (pagadoPor=AF, pagadoA=externo destino). Necesitamos el row
  // PAR para hidratar la otra punta externa correctamente. Por eso
  // resolvemos `lados` desde editing+editingPair en ese modo.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const tipo = editing.tipo;
      const fechaForm =
        tipo === "esporadico" && editing.fecha ? dayjs(editing.fecha) : null;

      // Lados del intermediario (solo en modo intermediario).
      let origenExterno = editing.pagadoPor;
      let destinoExterno = editing.pagadoA;
      if (mode === "intermediario") {
        if (editing.rolDeAlojaFacil === "recibe") {
          // editing es el lado que recibe: pagadoPor=origen, pagadoA=AF.
          origenExterno = editing.pagadoPor;
          destinoExterno = editingPair?.pagadoA ?? null;
        } else if (editing.rolDeAlojaFacil === "paga") {
          // editing es el lado que paga: pagadoPor=AF, pagadoA=destino.
          origenExterno = editingPair?.pagadoPor ?? null;
          destinoExterno = editing.pagadoA;
        }
        // Compat legacy: rows con rol='intermediario' (1-row, anteriores
        // a este round) tienen ambas puntas en su propio JSON.
      }

      form.setFieldsValue({
        tipo,
        monto: editing.monto,
        fecha: fechaForm,
        fechaPeriodica: editing.fechaPeriodica ?? 1,
        // Vigencia del regular. Si viene NULL es un regular anterior al round
        // 2026-07-26 ("sin vigencia registrada"): el campo queda vacío y el
        // admin debe completarlo para poder guardar.
        vigenciaInicio: editing.vigenciaInicio ? dayjs(editing.vigenciaInicio) : undefined,
        vigenciaFin: editing.vigenciaFin ? dayjs(editing.vigenciaFin) : undefined,
        categoria: editing.categoria,
        descripcion: editing.descripcion ?? "",
        propiedadId: editing.propiedadId ?? undefined,
        // pagadoA del form = receptor visible (admin lo elige). Para
        // gasto: AF paga, admin elige a quién. Para intermediario:
        // destino externo del lado 'paga'.
        pagadoA_rol:
          mode === "gasto"
            ? ROL_BY_TIPO[editing.pagadoA?.tipo ?? -1] ?? undefined
            : mode === "intermediario"
              ? ROL_BY_TIPO[destinoExterno?.tipo ?? -1] ?? undefined
              : undefined,
        pagadoA_usuarioId:
          mode === "intermediario"
            ? destinoExterno?.usuario_id ?? undefined
            : editing.pagadoA?.usuario_id ?? undefined,
        pagadoA_recipientId:
          mode === "intermediario"
            ? destinoExterno?.recipient_id ?? undefined
            : editing.pagadoA?.recipient_id ?? undefined,
        // Selector property-centric de propietario (round 2026-06-05): si
        // este lado es propietario (tipo=1), el valor del Select ES la
        // propiedad vinculada al flujo. Se reconstruye desde
        // editing.propiedadId.
        pagadoA_propiedadId:
          mode === "intermediario"
            ? destinoExterno?.tipo === 1
              ? editing.propiedadId ?? undefined
              : undefined
            : editing.pagadoA?.tipo === 1
              ? editing.propiedadId ?? undefined
              : undefined,
        // pagadoPor del form = pagador visible (admin lo elige). Para
        // ingreso: admin elige quién paga a AF. Para intermediario:
        // origen externo del lado 'recibe'.
        pagadoPor_rol:
          mode === "ingreso"
            ? ROL_BY_TIPO[editing.pagadoPor?.tipo ?? -1] ?? undefined
            : mode === "intermediario"
              ? ROL_BY_TIPO[origenExterno?.tipo ?? -1] ?? undefined
              : undefined,
        pagadoPor_usuarioId:
          mode === "intermediario"
            ? origenExterno?.usuario_id ?? undefined
            : editing.pagadoPor?.usuario_id ?? undefined,
        pagadoPor_recipientId:
          mode === "intermediario"
            ? origenExterno?.recipient_id ?? undefined
            : editing.pagadoPor?.recipient_id ?? undefined,
        pagadoPor_propiedadId:
          mode === "intermediario"
            ? origenExterno?.tipo === 1
              ? editing.propiedadId ?? undefined
              : undefined
            : editing.pagadoPor?.tipo === 1
              ? editing.propiedadId ?? undefined
              : undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        tipo: "esporadico",
        fecha: ahoraChile(),
        fechaPeriodica: 1,
        // Un regular nuevo arranca vigente desde el mes actual, sin término.
        vigenciaInicio: ahoraChile(),
        vigenciaFin: undefined,
      });
    }
  }, [open, editing, editingPair, mode, form]);

  // Filtrado de usuarios por rol del lado correspondiente. Se usa para los
  // selects de **aseador** (tipo=3) y **trabajador** (tipo=4), que sí
  // refieren a un usuario concreto del sistema.
  //
  // Round 2026-06-05 — el rol "propietario" YA NO se resuelve por acá. El
  // selector de propietario pasó a ser property-centric (se elige una
  // PROPIEDAD con sus dueños, no un usuario suelto) y se alimenta del
  // catálogo `propPropietarios` (endpoint /empresa/propietarios-propiedades).
  // Esto elimina el viejo workaround `cliente → propietario` que mostraba
  // clientes en el bucket de propietarios y causaba la ambigüedad que
  // reportó el owner.
  const usuariosByRol = useMemo(() => {
    const byRol: Record<string, UsuarioFinanzas[]> = {};
    for (const u of usuarios) {
      const bucket = String(u.rol ?? "").trim();
      if (!byRol[bucket]) byRol[bucket] = [];
      byRol[bucket].push(u);
    }
    return byRol;
  }, [usuarios]);

  // ─── Helper para construir el PagoEntidad desde los campos del form ──
  const buildPago = (
    rol: RolKey | undefined,
    usuarioId: number | undefined,
    recipientId: number | undefined,
    propietarioPropiedadId?: number | undefined,
  ) => {
    if (!rol) return null;
    const opt = ROL_OPTIONS.find((r) => r.value === rol);
    if (!opt) return null;
    if (opt.tipo === 5) {
      if (!recipientId) {
        getMsg()?.error("Selecciona un receptor externo");
        return null;
      }
      const recip = recipients.find((r) => r.id === recipientId);
      return {
        tipo: 5 as FlujoPartyTipo,
        nombre: recip?.nombre ?? "Receptor externo",
        recipient_id: recipientId,
      };
    }
    if (opt.tipo === 1) {
      // Cliente — PROPERTY-CENTRIC. El admin elige una PROPIEDAD; el flujo se
      // ancla a ella (`propiedadId`, que el Select setea aparte y viaja en el
      // payload). Round 2026-06-20 (decisión del owner, regla canónica §10.3.1):
      // guardamos al CLIENTE-gestor (con login, quien AF liquida), no al dueño
      // legal. En el JSON del pago:
      //   - `nombre`: el nombre del cliente (la PROPIEDAD la muestra la grilla
      //     como label primario vía `renderContraparte`); fallback al nombre de
      //     la propiedad si no hay cliente cargado.
      //   - `usuario_id`: el id del cliente, o null si no hay cliente asignado.
      if (!propietarioPropiedadId) {
        getMsg()?.error("Selecciona la propiedad");
        return null;
      }
      const entry = propPropietarios.find(
        (p) => p.propiedadId === propietarioPropiedadId,
      );
      const propNombre = entry?.nombrePropiedad ?? "Propiedad";
      const cliente = entry?.cliente ?? null;
      return {
        tipo: 1 as FlujoPartyTipo,
        nombre: cliente ? cliente.nombre : propNombre,
        usuario_id: cliente ? cliente.id : null,
      };
    }
    if (opt.tipo === 3 || opt.tipo === 4) {
      // Aseador / trabajador — ligados a un usuario concreto del sistema.
      // usuario_id requerido por el backend (tipo=3) para que el aseador
      // vea el flujo en su panel.
      if (!usuarioId) {
        getMsg()?.error(`Selecciona el usuario ${opt.label}`);
        return null;
      }
      const u = usuarios.find((x) => x.id === usuarioId);
      return {
        tipo: opt.tipo,
        nombre: u?.nombre ?? opt.label,
        usuario_id: usuarioId,
      };
    }
    // tipo 2 (arrendatario) — entidad abstracta, sin usuario_id.
    return {
      tipo: opt.tipo,
      nombre: opt.label,
      usuario_id: null,
    };
  };

  // ─── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const pagadoA =
        mode === "gasto" || mode === "intermediario"
          ? buildPago(
              values.pagadoA_rol,
              values.pagadoA_usuarioId,
              values.pagadoA_recipientId,
              values.pagadoA_propiedadId,
            )
          : pagoEntidadAF();
      const pagadoPor =
        mode === "ingreso" || mode === "intermediario"
          ? buildPago(
              values.pagadoPor_rol,
              values.pagadoPor_usuarioId,
              values.pagadoPor_recipientId,
              values.pagadoPor_propiedadId,
            )
          : pagoEntidadAF();

      if (!pagadoA || !pagadoPor) {
        setSubmitting(false);
        return;
      }

      // En modo intermediario, validar que las dos puntas sean entidades
      // distintas. Si tienen el mismo tipo + usuario_id (o ambos sin id),
      // bloquear con error.
      if (mode === "intermediario") {
        if (pagadoA.tipo === pagadoPor.tipo) {
          // AF (0) y arrendatario (2) son entidades únicas: dos del mismo
          // tipo = la misma entidad. (Propietario [1] NO entra acá: dos
          // propietarios pueden ser propiedades distintas; se comparan por
          // propiedadId abajo.)
          const sameSimple = [0, 2].includes(pagadoA.tipo);
          // Propietario: misma entidad solo si es la MISMA propiedad.
          const samePropietario =
            pagadoA.tipo === 1 &&
            values.pagadoA_propiedadId != null &&
            values.pagadoA_propiedadId === values.pagadoPor_propiedadId;
          const sameUser =
            pagadoA.usuario_id != null &&
            pagadoA.usuario_id === pagadoPor.usuario_id;
          const sameRecipient =
            pagadoA.recipient_id != null &&
            pagadoA.recipient_id === pagadoPor.recipient_id;
          if (sameSimple || samePropietario || sameUser || sameRecipient) {
            getMsg()?.error("Origen y destino del intermediario no pueden ser la misma entidad");
            setSubmitting(false);
            return;
          }
        }
      }

      const rol: FlujoCreatePayload["rolDeAlojaFacil"] =
        mode === "gasto" ? "paga" : mode === "ingreso" ? "recibe" : "intermediario";

      const payload: FlujoCreatePayload = {
        monto: Number(values.monto),
        tipo: values.tipo,
        fecha:
          values.tipo === "esporadico" && values.fecha
            ? dayjs(values.fecha).format("YYYY-MM-DD")
            : null,
        fechaPeriodica:
          values.tipo === "regular" ? Number(values.fechaPeriodica) : null,
        vigenciaInicio:
          values.tipo === "regular" && values.vigenciaInicio
            ? dayjs(values.vigenciaInicio).format("YYYY-MM-DD")
            : null,
        vigenciaFin:
          values.tipo === "regular" && values.vigenciaFin
            ? dayjs(values.vigenciaFin).format("YYYY-MM-DD")
            : null,
        pagadoPor,
        pagadoA,
        rolDeAlojaFacil: rol,
        categoria: values.categoria,
        descripcion: values.descripcion ?? "",
        propiedadId: values.propiedadId ?? null,
      };

      // Round 2026-05-13 — el modo intermediario tiene endpoints
      // dedicados que crean / actualizan AMBOS rows del par en una
      // transacción atómica. Sin esto, el create dejaría el segundo row
      // visible solo tras un refetch (race condition) y el edit afectaría
      // un solo lado (perdiéndose el cambio del otro pagador externo).
      if (mode === "intermediario") {
        const response = editing
          ? await actualizarFlujoIntermediario(editing.id, payload)
          : await crearFlujo(payload);
        getMsg()?.success(
          editing ? "Intermediario actualizado (2 movimientos)" : "Intermediario creado (2 movimientos)",
        );
        const archivos = !editing ? await subirPendientes(response.data.id) : null;
        const actualizado = archivos ? { ...response.data, imagenes: archivos } : response.data;
        const pair = response.pair && archivos ? {
          ingreso: response.pair.ingreso.id === actualizado.id ? actualizado : response.pair.ingreso,
          gasto: response.pair.gasto.id === actualizado.id ? actualizado : response.pair.gasto,
        } : response.pair;
        onSaved(actualizado, pair);
      } else {
        const saved = editing
          ? await actualizarFlujo(editing.id, payload)
          : (await crearFlujo(payload)).data;
        getMsg()?.success(editing ? "Flujo actualizado" : "Flujo creado");
        const archivos = !editing ? await subirPendientes(saved.id) : null;
        onSaved(archivos ? { ...saved, imagenes: archivos } : saved);
      }
    } catch (err: any) {
      // Si es error de validación del form, AntD ya lo muestra inline.
      if (err?.errorFields) return;
      getMsg()?.error(err?.message ?? "Error al guardar el flujo");
    } finally {
      setSubmitting(false);
    }
  };

  async function subirPendientes(id: number): Promise<FlujoFile[] | null> {
    if (!archivosPendientes.length) return null;
    const fallidos: string[] = [];
    const subidos: FlujoFile[] = [];
    for (const archivo of archivosPendientes) {
      try { const subido = await subirFlujoArchivo(id, archivo); if (subido) subidos.push(subido); else fallidos.push(archivo.name); }
      catch { fallidos.push(archivo.name); }
    }
    if (fallidos.length) getMsg()?.warning(`El flujo se creó, pero no se pudieron adjuntar: ${fallidos.join(", ")}. Agrégalos desde Adjuntos.`);
    return subidos;
  }

  // Catálogo de Selects de contraparte (filtra AF si el modo lo requiere).
  const rolOptionsParaContraparte = ROL_OPTIONS;

  return (
    <Modal
      title={
        <span style={{ color: meta.color, fontWeight: 600 }}>
          {meta.icon} {editing ? meta.titleEdit : meta.title}
        </span>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText={editing ? "Actualizar" : "Crear"}
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={700}
      destroyOnClose
    >
      <Alert
        type={
          mode === "gasto" ? "error" : mode === "ingreso" ? "success" : "info"
        }
        showIcon
        message={meta.alertMessage}
        description={meta.alertDescription}
        style={{ marginBottom: 16 }}
      />

      {esFlujoDerivadoInventario && (
        <Alert
          type={esRecuperacionInventario ? "info" : "warning"}
          showIcon
          message="📦 Este flujo proviene de una compra de inventario"
          description={
            esRecuperacionInventario
              ? "Es el cobro al cliente por el producto entregado. El MONTO sí es editable: puedes cobrar el costo, un parcial, más, o $0 si lo regalas. La categoría y la contraparte quedan fijas (derivadas del producto y la propiedad)."
              : "El monto, la categoría y la contraparte son read-only acá. Para cambiar precio o cantidad, edita el producto en /inventario y el flujo se sincroniza automáticamente. Para eliminarlo, borra el producto."
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" preserve={false}>
        {/* ── Tipo (esporádico vs regular) ─────────────────────── */}
        <Form.Item
          name="tipo"
          label="Frecuencia"
          rules={[{ required: true }]}
          tooltip="Esporádico = pago puntual con fecha. Regular = se repite mensualmente el día N (1-28)."
          initialValue="esporadico"
        >
          <Select
            options={[
              { value: "esporadico", label: "Esporádico (una sola vez)" },
              { value: "regular", label: "Regular (mensual)" },
            ]}
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const tipo = form.getFieldValue("tipo");
            if (tipo === "regular") {
              return (
                <>
                  <Form.Item
                    name="fechaPeriodica"
                    label="Día del mes (1-28)"
                    rules={[
                      { required: true, message: "Indica el día del mes" },
                      {
                        type: "number",
                        min: 1,
                        max: 28,
                        message: "Día válido: 1 a 28",
                      },
                    ]}
                  >
                    <InputNumber min={1} max={28} style={{ width: "100%" }} />
                  </Form.Item>

                  {/* Vigencia (round 2026-07-26): un regular rige SOLO dentro
                      de estas fechas. Se elige por MES para no dejar meses a
                      medias (un regular se paga un día fijo del mes). */}
                  <Form.Item
                    name="vigenciaInicio"
                    label="Vigente desde (mes)"
                    tooltip="Desde qué mes empieza a cobrarse este gasto. Antes de esta fecha no aparece ni suma en ningún reporte."
                    rules={[{ required: true, message: "Indica desde qué mes rige" }]}
                  >
                    <DatePicker
                      picker="month"
                      style={{ width: "100%" }}
                      format="MM/YYYY"
                      placeholder="Mes de inicio"
                    />
                  </Form.Item>
                  <Form.Item
                    name="vigenciaFin"
                    label="Vigente hasta (mes, opcional)"
                    tooltip="Último mes en que se cobra. Déjalo vacío si el gasto sigue indefinidamente; puedes ponerle término más adelante."
                    dependencies={["vigenciaInicio"]}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const ini = getFieldValue("vigenciaInicio");
                          if (!value || !ini) return Promise.resolve();
                          return value.isBefore(ini, "month")
                            ? Promise.reject(
                                new Error(
                                  "El término no puede ser anterior al inicio",
                                ),
                              )
                            : Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <DatePicker
                      picker="month"
                      style={{ width: "100%" }}
                      format="MM/YYYY"
                      placeholder="Sin término (indefinido)"
                    />
                  </Form.Item>
                </>
              );
            }
            return (
              <Form.Item
                name="fecha"
                label="Fecha"
                rules={[{ required: true, message: "Selecciona la fecha" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  inputReadOnly
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        {/* ── Monto ─────────────────────────────────────────────────── */}
        <Form.Item
          name="monto"
          label="Monto (CLP)"
          rules={[
            { required: true, message: "Indica el monto" },
            // La recuperación admite $0 (regalo); el resto exige monto > 0.
            {
              type: "number",
              min: esRecuperacionInventario ? 0 : 1,
              message: esRecuperacionInventario ? "Monto ≥ 0" : "Monto > 0",
            },
          ]}
        >
          <InputNumber<number>
            style={{ width: "100%" }}
            min={0}
            step={1000}
            disabled={montoReadOnly}
            formatter={(v) =>
              v != null ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""
            }
            parser={
              // C2 (round 2026-06-20): conservar SOLO dígitos. CLP es entero
              // (sin decimales) y así el signo "-" no puede colarse ni por
              // pegado ni por teclado → nunca un monto negativo en el form.
              ((v) =>
                Number((v ?? "").toString().replace(/[^\d]/g, "")) ||
                0) as (v: string | undefined) => number
            }
          />
        </Form.Item>

        {/* ── Categoría ─────────────────────────────────────────────── */}
        <Form.Item
          name="categoria"
          label="Categoría"
          rules={[{ required: true, message: "Elige una categoría" }]}
          tooltip="Clasificación contable del flujo. 'Bono' y 'Multa' aplican típicamente a movimientos extra al aseador; las demás categorías son operativas (cuentas, reparaciones, transporte, etc.)."
        >
          <Select
            showSearch
            disabled={esFlujoDerivadoInventario}
            optionFilterProp="label"
            placeholder="Selecciona una categoría"
            options={[
              ...(editing?.categoria === "sueldo"
                ? [{ value: "sueldo", label: "Reembolso (categoría anterior)" }]
                : []),
              ...FLUJO_CATEGORIAS.map((c) => ({
                value: c,
                label: CATEGORIA_LABEL[c] ?? c,
              })),
            ]}
          />
        </Form.Item>

        {/* ── Pagado por / Pagado a según modo ───────────────────── */}
        {mode === "gasto" && (
          <>
            <div
              style={{
                padding: "10px 12px",
                background: meta.bgColor,
                borderRadius: 6,
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              <Tag color="default">Pagado por</Tag>{" "}
              <strong>Aloja Fácil</strong> (fijo)
            </div>
            <ContraparteFields
              prefix="pagadoA"
              label={meta.sideLabel}
              tooltip={meta.sideTooltip}
              rolOptions={rolOptionsParaContraparte}
              usuariosByRol={usuariosByRol}
              recipients={recipients}
              propPropietarios={propPropietarios}
              propiedades={propiedades}
              disabled={esFlujoDerivadoInventario}
              form={form}
            />
          </>
        )}

        {mode === "ingreso" && (
          <>
            <ContraparteFields
              prefix="pagadoPor"
              label={meta.sideLabel}
              tooltip={meta.sideTooltip}
              rolOptions={rolOptionsParaContraparte}
              usuariosByRol={usuariosByRol}
              recipients={recipients}
              propPropietarios={propPropietarios}
              propiedades={propiedades}
              disabled={esFlujoDerivadoInventario}
              form={form}
            />
            <div
              style={{
                padding: "10px 12px",
                background: meta.bgColor,
                borderRadius: 6,
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              <Tag color="default">Pagado a</Tag>{" "}
              <strong>Aloja Fácil</strong> (fijo)
            </div>
          </>
        )}

        {mode === "intermediario" && (
          <>
            <ContraparteFields
              prefix="pagadoPor"
              label="Origen — quién entrega el dinero"
              tooltip="La parte que paga el monto. AF Empresa lo recibe momentáneamente para transferirlo a la otra punta."
              rolOptions={rolOptionsParaContraparte}
              usuariosByRol={usuariosByRol}
              recipients={recipients}
              propPropietarios={propPropietarios}
              propiedades={propiedades}
              disabled={esFlujoDerivadoInventario}
              form={form}
            />
            <ContraparteFields
              prefix="pagadoA"
              label="Destino — a quién AF transfiere"
              tooltip="La parte que recibe el monto. AF Empresa se lo entrega después de cobrar al origen."
              rolOptions={rolOptionsParaContraparte}
              usuariosByRol={usuariosByRol}
              recipients={recipients}
              propPropietarios={propPropietarios}
              propiedades={propiedades}
              disabled={esFlujoDerivadoInventario}
              form={form}
            />
          </>
        )}

        {/* ── Propiedad y descripción ─────────────────────────────── */}
        {/*
          Round 2026-05-31 (ter) — `propiedadId` se sincroniza con el
          propietario elegido en ContraparteFields:
            • Si el propietario tiene 1 propiedad → la propiedad se setea.
            • Si tiene 0 o ≥2 → la propiedad se LIMPIA.
          La sincronización es one-way (propietario → propiedad): elegir
          propiedad acá NO autorrellena propietario, porque puedes tener
          un cobro contra la propiedad que no involucra al dueño (ej.
          cargo al arrendatario sobre esa unidad).

          Round 2026-06-01 — `propiedadId` pasa a ser REQUERIDO cuando
          alguna contraparte del flujo es propietario (rol="propietario"
          en cualquiera de los dos lados). Razón: el panel
          `/finanzas-cliente` filtra los flujos por `propiedad_id IN
          (propiedades del cliente)`, NO por `pagado_a.usuario_id`. Sin
          una propiedad vinculada, el gasto/ingreso queda invisible para
          el cliente aunque su usuario_id esté en el JSON de pagado_a.
          Ver finanzas.controller.js ~L1812.
          Sigue siendo OPCIONAL cuando ninguna contraparte es
          propietario (ej. pago a aseador, trabajador, receptor externo,
          arrendatario), porque esos flujos no tienen panel propietario
          al que reportar.
        */}
        <Form.Item noStyle shouldUpdate>
          {() => {
            const pagadoARol = form.getFieldValue("pagadoA_rol");
            const pagadoPorRol = form.getFieldValue("pagadoPor_rol");
            const hayPropietario =
              pagadoARol === "propietario" || pagadoPorRol === "propietario";
            return (
              <Form.Item
                name="propiedadId"
                label={
                  hayPropietario
                    ? "Propiedad (requerida)"
                    : "Propiedad (opcional)"
                }
                tooltip={
                  hayPropietario
                    ? "Cuando una de las puntas es un propietario, la propiedad es OBLIGATORIA. El panel financiero del cliente filtra los flujos por propiedad, así que sin esta vinculación el cliente no vería este movimiento aunque le corresponda."
                    : "Se sincroniza con el propietario si lo eliges: si tiene una sola propiedad, se autorrellena; si tiene varias o ninguna, queda vacía. Puedes cambiarla manualmente."
                }
                rules={
                  hayPropietario
                    ? [
                        {
                          required: true,
                          message:
                            "Elige la propiedad afectada — sin esto el propietario no verá el flujo en su panel financiero",
                        },
                      ]
                    : []
                }
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={
                    hayPropietario
                      ? "Selecciona la propiedad asociada al propietario"
                      : "Vincular a una propiedad si aplica"
                  }
                  options={propiedades.map((p) => ({
                    value: p.id,
                    label: formatPropertyName(p.nombre),
                  }))}
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item
          name="descripcion"
          label="Descripción"
          tooltip="Detalle del movimiento (ej. 'Reparación cocina depto A')"
        >
          <TextArea rows={3} placeholder="Detalles del flujo..." />
        </Form.Item>

        {/* ── Adjuntos: galería de fotos y documentos ──────────────────
            Mismo patrón que el resto de la app (info-huésped, tareas):
            arrastrar / click / Ctrl+V, previsualizar, descargar (uno o
            varios) y borrar. Los adjuntos se gestionan aparte del form
            (van directo a `gastos.imagenes` vía endpoints dedicados), por
            eso el Form.Item NO lleva `name`. En modo creación el manager
            muestra un aviso de "guarda primero" (necesita el id del flujo). */}
        <Form.Item
          label="Adjuntos (fotos y documentos)"
          tooltip="Sube fotos, boletas, facturas o comprobantes que respalden este movimiento. Soporta arrastrar, pegar (Ctrl+V), previsualizar, descargar y borrar uno o varios."
        >
          {editing ? <FlujoFilesManager flujoId={idParaAdjuntos(editing, editingPair)} modalOpen={open} /> : <>
            <DragDropUpload accept={FLUJO_ACCEPT} multiple onFile={agregarArchivo} disabled={submitting} title="Adjunta fotos, videos o documentos" hint="Arrastra, selecciona o pega archivos; se subirán al crear el flujo." />
            {archivosPendientes.map((archivo, i) => <div key={`${archivo.name}-${i}`} style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span>{archivo.name}</span><Button size="small" disabled={submitting} onClick={() => setArchivosPendientes((prev) => prev.filter((_, n) => n !== i))}>Quitar</Button>
            </div>)}
          </>}
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Sub-componente: campos de contraparte (rol + usuario/recipient) ────────
//
// Pinta 2 Selects: el rol (Propietario/Arrendatario/...) y, según el rol,
// un Select dependiente para el usuario concreto (o el receptor externo).
// Cada campo del Form lleva el prefix correspondiente (pagadoA o pagadoPor)
// para coexistir en el mismo Form en modo intermediario.

interface ContraparteFieldsProps {
  prefix: "pagadoA" | "pagadoPor";
  label: string;
  tooltip: string;
  rolOptions: Array<{ value: RolKey; label: string; tipo: FlujoPartyTipo }>;
  usuariosByRol: Record<string, UsuarioFinanzas[]>;
  recipients: ExpenseRecipient[];
  /**
   * Catálogo property-centric (round 2026-06-05; cliente desde 2026-06-20).
   * Cuando el rol elegido es tipo=1 ("Cliente"), el Select dependiente lista
   * PROPIEDADES con su cliente-gestor y al elegir una vincula el flujo a esa
   * propiedad (setea `propiedadId`).
   */
  propPropietarios: PropiedadConPropietarios[];
  /**
   * Lista completa de propiedades (incluye deshabilitadas). Solo se usa como
   * FALLBACK: si al editar un flujo de propietario su propiedad fue
   * deshabilitada después (y ya no está en `propPropietarios`), igual la
   * mostramos con su nombre + "(deshabilitada)" para no dejar un id pelado.
   */
  propiedades: PropiedadOpt[];
  /**
   * Deshabilita la edición de la contraparte (round 2026-06-05). Se usa para
   * flujos derivados de inventario, cuyo monto/categoría/contraparte son
   * read-only (la fuente de verdad es el producto en /inventario).
   */
  disabled?: boolean;
  form: any;
}

function ContraparteFields({
  prefix,
  label,
  tooltip,
  rolOptions,
  usuariosByRol,
  recipients,
  propPropietarios,
  propiedades,
  disabled = false,
  form,
}: ContraparteFieldsProps) {
  const fieldRol = `${prefix}_rol`;
  const fieldUsuario = `${prefix}_usuarioId`;
  const fieldRecipient = `${prefix}_recipientId`;
  const fieldPropiedad = `${prefix}_propiedadId`;

  return (
    <>
      <Form.Item
        name={fieldRol}
        label={
          tooltip ? (
            <Tooltip title={tooltip}>
              <span>
                {label} <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.6 }} />
              </span>
            </Tooltip>
          ) : (
            label
          )
        }
        rules={[{ required: true, message: "Elige una contraparte" }]}
      >
        <Select
          placeholder="Selecciona tipo"
          disabled={disabled}
          onChange={() => {
            // Limpia los campos dependientes al cambiar el rol.
            form.setFieldsValue({
              [fieldUsuario]: undefined,
              [fieldRecipient]: undefined,
              [fieldPropiedad]: undefined,
            });
          }}
          options={rolOptions.map((r) => ({ value: r.value, label: r.label }))}
        />
      </Form.Item>

      <Form.Item noStyle shouldUpdate>
        {() => {
          const rol = form.getFieldValue(fieldRol) as RolKey | undefined;
          if (!rol) return null;
          // Receptor externo — Select del catálogo.
          if (rol === "receptor_externo") {
            return (
              <Form.Item
                name={fieldRecipient}
                label="Receptor externo (del catálogo)"
                tooltip="Cualquier OTRA EMPRESA que recibe dinero de Aloja Fácil y no es usuaria del sistema: servicios básicos (internet, agua), impuestos, tiendas y proveedores de insumos, servicios profesionales (por ejemplo un contador) o el receptor genérico 'Otro'. NUNCA es una lavandería: los pagos a lavandería (AF interna o General Externa) se registran en /lavanderia — anotarlos acá los deja fuera de esa reconciliación y los cuenta dos veces en la utilidad."
                rules={[
                  { required: true, message: "Selecciona un receptor externo" },
                ]}
              >
                <Select
                  showSearch
                  disabled={disabled}
                  optionFilterProp="label"
                  placeholder="Buscar receptor..."
                  options={recipients.map((r) => ({ value: r.id, label: r.nombre }))}
                />
              </Form.Item>
            );
          }
          // Arrendatario — entidad abstracta, sin Select de usuario.
          if (rol === "arrendatario") {
            return null;
          }
          // Propietario — PROPERTY-CENTRIC (round 2026-06-05, pedido del
          // owner). En vez de elegir un usuario suelto, el admin elige una
          // PROPIEDAD y ve sus dueños inline ("Nombre Propiedad —
          // Propietario1 · Propietario2 · … · PropietarioN"). NUNCA aparece
          // el cliente. Al elegir, el flujo se vincula a esa propiedad
          // (seteamos `propiedadId`), que es lo que hace que las finanzas
          // queden ligadas a la propiedad (y su cliente la vea en su panel).
          //
          // Esto reemplaza el viejo Select de usuario (que mostraba
          // clientes mapeados a "propietario" y obligaba a recordar de
          // memoria a qué propiedad correspondía cada uno). El catálogo
          // viene de `propPropietarios` (endpoint property-centric).
          //
          // Buscable tipeando el nombre de la propiedad O de cualquier
          // dueño (normalizamos sin acentos / case-insensitive). Las
          // propiedades sin dueño cargado se muestran "(sin propietario)"
          // para poder vincular el flujo igual.
          if (rol === "propietario") {
            const norm = (s: string) =>
              s
                .toLowerCase()
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "");
            const options = propPropietarios.map((p) => {
              // Round 2026-06-20: mostramos el CLIENTE-gestor (con login, quien
              // AF liquida), no el dueño legal. "(sin cliente)" si la propiedad
              // no tiene cliente asignado.
              const clientePart = p.cliente?.nombre || "(sin cliente)";
              return {
                value: p.propiedadId,
                label: `${formatPropertyName(p.nombrePropiedad)} — ${clientePart}`,
                searchKey: norm(
                  `${p.nombrePropiedad} ${p.cliente?.nombre ?? ""}`,
                ),
              };
            });
            // Fallback (round 2026-06-05): si estamos editando un flujo cuya
            // propiedad fue deshabilitada después (ya no está en el catálogo
            // property-centric), inyectamos una opción con su nombre +
            // "(deshabilitada)" para no mostrar un id pelado. Si ni siquiera
            // está en la lista completa, mostramos el id como último recurso.
            const selected = form.getFieldValue(fieldPropiedad) as
              | number
              | undefined;
            if (
              selected != null &&
              !options.some((o) => o.value === selected)
            ) {
              const prop = propiedades.find((p) => p.id === selected);
              options.unshift({
                value: selected,
                label: `${formatPropertyName(prop?.nombre) || `Propiedad #${selected}`} — (deshabilitada)`,
                searchKey: norm(prop?.nombre ?? String(selected)),
              });
            }
            return (
              <Form.Item
                name={fieldPropiedad}
                label="Propiedad y cliente"
                tooltip="Elige la propiedad: se muestra su cliente (el de login, a quien AF le liquida). El flujo queda vinculado a esa propiedad para que aparezca en sus finanzas. Busca por el nombre de la propiedad o del cliente."
                rules={[
                  {
                    required: true,
                    message: "Selecciona la propiedad",
                  },
                ]}
              >
                <Select
                  showSearch
                  disabled={disabled}
                  placeholder="Buscar propiedad o cliente..."
                  filterOption={(input, option: any) => {
                    if (!input) return true;
                    return option?.searchKey?.includes(norm(input)) ?? false;
                  }}
                  onChange={(propiedadId) => {
                    // One-way: elegir la propiedad/propietario vincula el
                    // flujo a esa propiedad (setea el campo "Propiedad").
                    form.setFieldsValue({
                      propiedadId: propiedadId ?? undefined,
                    });
                  }}
                  options={options}
                />
              </Form.Item>
            );
          }
          // Aseador / Trabajador — Select de usuario concreto del sistema,
          // con label "Nombre — PROPIEDAD" vía helper canónico
          // `userSelectOption` (utils/userDisplay.ts) y búsqueda sin
          // acentos / case-insensitive (también matchea las propiedades
          // del usuario, incluidas las truncadas a "+N" en el label).
          const filtered = (usuariosByRol[rol] ?? []) as UsuarioFinanzas[];
          const placeholder =
            rol === "aseador"
              ? "Selecciona el aseador"
              : "Selecciona el trabajador";

          const norm = (s: string) =>
            s
              .toLowerCase()
              .normalize("NFD")
              .replace(/\p{Diacritic}/gu, "");
          const options = filtered.map((u) => {
            const opt = userSelectOption(u);
            const props = Array.isArray(u.propiedades) ? u.propiedades : [];
            return {
              ...opt,
              searchKey: norm(`${opt.label} ${props.join(" ")}`),
            };
          });

          return (
            <Form.Item
              name={fieldUsuario}
              label={`Usuario ${rol}`}
              rules={[{ required: true, message: "Selecciona un usuario" }]}
            >
              <Select
                showSearch
                disabled={disabled}
                placeholder={placeholder}
                filterOption={(input, option: any) => {
                  if (!input) return true;
                  return option?.searchKey?.includes(norm(input)) ?? false;
                }}
                options={options}
              />
            </Form.Item>
          );
        }}
      </Form.Item>
    </>
  );
}
